import { Service } from "@angular/core";

import { Constants } from "../constants/constants";
import { PageMode } from "../constants/enums";
import { FormTab, ItemAction } from "../models";

@Service()
export class ProjectsService {
  public getPermission(projectType: number) {
    switch (projectType) {
      case Constants.PROJECT_TYPES_DS3.STRATEGIC_PROJECT:
        return Constants.PROJECT_PERMISSIONS_DS3.STRATEGIC_PROJECT;
      case Constants.PROJECT_TYPES_DS3.REGULAR_TASK:
        return Constants.PROJECT_PERMISSIONS_DS3.REGULAR_TASK;
      case Constants.PROJECT_TYPES_DS3.M1:
        return Constants.PROJECT_PERMISSIONS_DS3.M1;
      default:
        return null;
    }
  }

  public getChildPermission(projectType: number) {
    switch (projectType) {
      case Constants.PROJECT_TYPES_DS3.STRATEGIC_PROJECT:
        return Constants.ACTIVITY_PERMISSIONS_DS3.STRATEGIC_ACTIVITY;
      case Constants.PROJECT_TYPES_DS3.REGULAR_TASK:
        return Constants.ACTIVITY_PERMISSIONS_DS3.OPERATIONAL_ACTIVITY;
      case Constants.PROJECT_TYPES_DS3.M1:
        return Constants.ACTIVITY_PERMISSIONS_DS3.M1_MILESTONE;
      default:
        return null;
    }
  }

  public getWorkflowId(projectType: number) {
    switch (projectType) {
      case Constants.PROJECT_TYPES_DS3.STRATEGIC_PROJECT:
        return Constants.PROJECT_WORKFLOW_DS3.STRATEGIC_PROJECT;
      case Constants.PROJECT_TYPES_DS3.REGULAR_TASK:
        return Constants.PROJECT_WORKFLOW_DS3.REGULAR_TASK;
      case Constants.PROJECT_TYPES_DS3.M1:
        return Constants.PROJECT_WORKFLOW_DS3.M1;
      default:
        return null;
    }
  }

  public getProjectTypeId(projectType: string) {
    switch (projectType) {
      case "strategic-project":
        return Constants.PROJECT_TYPES_DS3.STRATEGIC_PROJECT;
      case "regular-task":
        return Constants.PROJECT_TYPES_DS3.REGULAR_TASK;
      case "m1":
        return Constants.PROJECT_TYPES_DS3.M1;
      default:
        return null;
    }
  }

  public getProjectPageConfig(projectType: string) {
    switch (projectType) {
      case "strategic-project":
        return {
          title: "breadcrumbs.initiative_module.strategic_project_list",
          tableConfig: Constants.MAIN_TABLE_LIST_CONF_KEY.PROJECTS_LIST,
          hideDeleteEditRules: [
            {
              propertyName: "editable",
              compareWith: false,
              operator: "equal",
            },
          ] as ItemAction[],
        };
      case "regular-task":
        return {
          title: "breadcrumbs.initiative_module.regular_task_list",
          tableConfig: Constants.MAIN_TABLE_LIST_CONF_KEY.PROJECTS_LIST,
          hideDeleteEditRules: [
            {
              propertyName: "editable",
              compareWith: false,
              operator: "equal",
            },
          ] as ItemAction[],
        };
      case "m1":
        return {
          title: "national_projects.project_names",
          tableConfig: Constants.MAIN_TABLE_LIST_CONF_KEY.M1_PROJECTS_LIST,
          hideDeleteEditRules: [
            {
              propertyName: "editable",
              compareWith: false,
              operator: "equal",
            },
          ] as ItemAction[],
        };
      default:
        return null;
    }
  }

  public getProjectEditPageConfig(projectType: string, pageMode: PageMode, tabsHasError: number[]) {
    const basicTabs: FormTab[] = [
      {
        id: "info",
        title: "common.form.label.information",
        hasError: tabsHasError.includes(0),
      },
      {
        id: "activities",
        title: "activities.title",
        hasError: tabsHasError.includes(1),
      },
      {
        id: "attachments",
        title: "kpi.attachments",
        hasError: tabsHasError.includes(2),
      },
      {
        id: "team-members",
        title: "national_projects.project_team_members.common_text",
        hasError: tabsHasError.includes(3),
      },
      {
        title: "common.links",
        hasError: tabsHasError.includes(4),
      },
    ];

    switch (projectType) {
      case "strategic-project":
        return {
          title:
            pageMode === PageMode.create
              ? "breadcrumbs.initiative_module.strategic_project_new"
              : "breadcrumbs.initiative_module.strategic_project_edit",
          listTitle: "breadcrumbs.initiative_module.strategic_project_list",
          tabs: [...basicTabs],
          formValidationKey: Constants.VALIDATORS_CONF_KEY.DS3_STRATEGIC_PROJECT_FORM,
        };
      case "regular-task":
        return {
          title:
            pageMode === PageMode.create
              ? "breadcrumbs.initiative_module.regular_task_new"
              : "breadcrumbs.initiative_module.regular_task_edit",
          listTitle: "breadcrumbs.initiative_module.regular_task_list",
          tabs: [...basicTabs],
          formValidationKey: Constants.VALIDATORS_CONF_KEY.DS3_REGULAR_TASK_PROJECT_FORM,
        };
      case "m1":
        basicTabs[1].title = "national_projects.milestones";
        return {
          title: pageMode === PageMode.create ? "national_projects.create_project" : "national_projects.edit_project",
          listTitle: "national_projects.project_names",
          tabs: [...basicTabs],
          formValidationKey: Constants.VALIDATORS_CONF_KEY.DS3_TRANSFORMATIONAL_PROJECT_FORM,
        };
      default:
        return null;
    }
  }

  public getProjectViewPageConfig(projectType: string) {
    switch (projectType) {
      case "strategic-project":
        return {
          listTitle: "breadcrumbs.initiative_module.strategic_project_list",
          title: "breadcrumbs.initiative_module.strategic_project_view",
        };
      case "regular-task":
        return {
          listTitle: "breadcrumbs.initiative_module.regular_task_list",
          title: "breadcrumbs.initiative_module.regular_task_view",
        };
      case "m1":
        return {
          listTitle: "national_projects.project_names",
          title: "national_projects.view_national_project",
        };
      default:
        return null;
    }
  }

  public prepareDataFormSubmit(data: Record<string, unknown>, isFinish: boolean): Record<string, unknown> {
    const { REMOVE, ACTIVE, DRAFT, IN_REVIEW } = Constants.OBJECT_STATUS;
    const isNew = !data.id;

    delete data.sectorsIds;
    if (isNew) delete data.id;
    if (isNew && isFinish) delete data.status;

    data.contributingEntities ??= [];

    const toArr = (val: unknown): Record<string, unknown>[] => (val as Record<string, unknown>[]) || [];

    const normalizeFakeId = (e: Record<string, unknown>) => {
      if (!e.id) {
        delete e.id;
        delete e.fakeId;
      }
    };

    const normalizeStatus = (e: Record<string, unknown>, allowedStatuses: number[]) => {
      if (isNew && !isFinish) delete e.status;
      else if (isFinish && Array.isArray(allowedStatuses) && !allowedStatuses.includes(e.status as number))
        e.status = DRAFT;
    };

    const normalizeItem = (e: Record<string, unknown>, allowedStatuses: number[]) => {
      normalizeFakeId(e);
      normalizeStatus(e, allowedStatuses);
    };

    toArr(data.projectTeamMembers).forEach((e) => normalizeItem(e, [REMOVE, ACTIVE, IN_REVIEW]));
    toArr(data.targetAudience).forEach((e) => normalizeItem(e, [REMOVE, ACTIVE]));
    toArr(data.expectedImpact).forEach((e) => normalizeItem(e, [REMOVE, ACTIVE]));
    toArr(data.comments).forEach((e) => normalizeItem(e, [REMOVE, ACTIVE]));

    return data;
  }
}
